import { useCallback, useEffect, useState } from "react";
import { ArrowsClockwise, Bell, CheckCircle, ShieldCheck, Warning } from "@phosphor-icons/react";
import api from "../lib/api";
import { confirmProductionWrite } from "../lib/diagnostics";
import { toast } from "sonner";
import {
  aggregateTrustedRunResults,
  TRUSTED_ACTIONS,
  TRUSTED_OPERATOR_LABELS,
  trustedConfirmationCopy,
  trustedNotificationsEnabled,
  trustedOperatorControls,
  trustedPhaseLabel,
} from "../lib/trustedAutomation";

const OPERATOR_KEYS = ["dcg", "llf"];

function TogglePill({ label, enabled }) {
  return (
    <span
      className={`px-2 py-0.5 rounded border text-[10px] uppercase tracking-wider font-semibold ${
        enabled
          ? "bg-emerald-500/15 text-emerald-200 border-emerald-500/30"
          : "bg-white/[0.04] text-white/45 border-white/10"
      }`}
    >
      {label}: {enabled ? "enabled" : "disabled"}
    </span>
  );
}

function ResultStat({ label, value }) {
  return (
    <div className="rounded border border-white/10 bg-white/[0.03] px-3 py-2">
      <div className="font-mono-num text-lg font-semibold text-white">{value ?? 0}</div>
      <div className="text-[10px] uppercase tracking-wider text-white/45">{label}</div>
    </div>
  );
}

export default function TrustedAutomationCard({
  diag,
  onRun,
  operatorKeys = OPERATOR_KEYS,
  compact = false,
}) {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(null);
  const [lastResult, setLastResult] = useState(null);

  const refreshStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/trusted-automation/status");
      setStatus(data);
      return data;
    } catch (e) {
      toast.error("Trusted automation status failed: " + (e.response?.data?.detail || e.message));
      return null;
    }
  }, []);

  useEffect(() => {
    refreshStatus();
  }, [refreshStatus]);

  const unreadCount = async () => {
    try {
      const { data } = await api.get("/admin/notifications/unread-count");
      return data.unread_count || 0;
    } catch {
      return 0;
    }
  };

  const runOperators = async (operatorKeys) => {
    const currentStatus = status || await refreshStatus();
    if (!currentStatus) return;
    const labels = operatorKeys.map((key) => TRUSTED_OPERATOR_LABELS[key] || key);
    const copy = trustedConfirmationCopy(currentStatus, labels, operatorKeys);
    if (!confirmProductionWrite(diag, copy)) return;

    setBusy(operatorKeys.join("+"));
    try {
      const beforeUnread = await unreadCount();
      const results = [];
      for (const operatorKey of operatorKeys) {
        const { data } = await api.post("/admin/trusted-automation/run-once", {
          operator_key: operatorKey,
          dry_run: false,
        });
        results.push(data);
      }
      const afterUnread = await unreadCount();
      const aggregate = aggregateTrustedRunResults(results, afterUnread - beforeUnread);
      setLastResult(aggregate);
      window.dispatchEvent(new CustomEvent("admin:notifications-refresh"));
      toast.success(
        `Trusted automation ran for ${aggregate.operators_run.join(" and ")}: ${aggregate.totals.scanned} scanned.`,
      );
      onRun?.(aggregate);
    } catch (e) {
      toast.error("Trusted automation failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setBusy(null);
    }
  };

  const openNotifications = () => {
    window.dispatchEvent(new CustomEvent("admin:open-notifications"));
  };

  const notificationsEnabled = trustedNotificationsEnabled(status);
  const phaseLabel = trustedPhaseLabel(status);
  const visibleOperatorKeys = operatorKeys.filter((key) => OPERATOR_KEYS.includes(key));

  return (
    <div
      className="rounded-lg border border-emerald-500/25 bg-emerald-500/[0.04] p-4 space-y-4"
      data-testid="trusted-automation-card"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-sm font-semibold text-emerald-100">
            <ShieldCheck size={18} weight="duotone" />
            Trusted Automation
          </div>
          <div className="text-[11px] text-white/60 mt-1">
            Public visibility remains the final gate.
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="px-2 py-1 rounded border border-emerald-500/30 bg-emerald-500/10 text-emerald-100 text-[11px] font-semibold">
            {phaseLabel}
          </span>
          <TogglePill label="Notifications" enabled={notificationsEnabled} />
        </div>
      </div>

      <div className={`grid gap-3 ${compact ? "md:grid-cols-1" : "md:grid-cols-2"}`}>
        {visibleOperatorKeys.map((operatorKey) => {
          const controls = trustedOperatorControls(status, operatorKey);
          return (
            <div
              key={operatorKey}
              className="rounded border border-white/10 bg-black/10 p-3"
              data-testid={`trusted-automation-operator-${operatorKey}`}
            >
              <div className="font-semibold text-white">{TRUSTED_OPERATOR_LABELS[operatorKey]}</div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {TRUSTED_ACTIONS.map(([field, label]) => (
                  <TogglePill key={field} label={label} enabled={controls[field] === true} />
                ))}
              </div>
              <button
                type="button"
                onClick={() => runOperators([operatorKey])}
                disabled={busy != null || !controls.scan_enabled}
                data-testid={`trusted-run-${operatorKey}`}
                className="mt-3 w-full py-2 rounded text-xs inline-flex items-center justify-center gap-1.5 bg-white/10 hover:bg-white/15 text-white disabled:opacity-45"
                title={!controls.scan_enabled ? "Scan is disabled for this operator" : ""}
              >
                <ArrowsClockwise size={12} weight="bold" />
                {busy === operatorKey ? "Running..." : `Run ${TRUSTED_OPERATOR_LABELS[operatorKey]}`}
              </button>
            </div>
          );
        })}
      </div>

      <div className="rounded border border-white/10 bg-white/[0.03] p-3 text-[11px] text-white/65 leading-relaxed">
        <Warning size={14} className="inline mr-1 text-amber-200" />
        Before running, the confirmation dialog will show exactly which backend actions are enabled.
        With Phase 1 settings, scan is enabled and apply, verify, publish and expire are disabled.
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {visibleOperatorKeys.length > 1 && (
          <button
            type="button"
            onClick={() => runOperators(visibleOperatorKeys)}
            disabled={busy != null || !visibleOperatorKeys.some((key) => trustedOperatorControls(status, key).scan_enabled)}
            data-testid="trusted-run-all"
            className="btn-gold px-4 py-2 rounded text-sm inline-flex items-center gap-1.5 disabled:opacity-50"
          >
            <ArrowsClockwise size={14} weight="bold" />
            {busy === visibleOperatorKeys.join("+") ? "Running..." : "Run Trusted Automation Now"}
          </button>
        )}
        <button
          type="button"
          onClick={refreshStatus}
          disabled={busy != null}
          data-testid="trusted-refresh-status"
          className="px-4 py-2 rounded text-sm bg-white/10 hover:bg-white/15 text-white inline-flex items-center gap-1.5 disabled:opacity-50"
        >
          <CheckCircle size={14} /> Refresh Trusted Automation Status
        </button>
        <button
          type="button"
          onClick={openNotifications}
          data-testid="trusted-view-notifications"
          className="px-4 py-2 rounded text-sm border border-white/15 text-white/80 hover:text-white inline-flex items-center gap-1.5"
        >
          <Bell size={14} /> View Notifications
        </button>
      </div>

      {lastResult && (
        <div
          className="rounded border border-white/10 bg-black/10 p-3"
          data-testid="trusted-run-result"
        >
          <div className="text-xs font-semibold text-white mb-2">
            Last run: {lastResult.operators_run.join(", ")}
          </div>
          <div className="mb-3 flex flex-wrap gap-2">
            {lastResult.results.map((item) => (
              <span
                key={item.operator_key || item.operator_name}
                className="px-2 py-0.5 rounded border border-white/10 bg-white/[0.04] text-[10px] uppercase tracking-wider text-white/60"
              >
                {item.operator_name}: {item.dry_run ? "dry-run" : "apply run"} · {item.status}
              </span>
            ))}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 lg:grid-cols-9 gap-2">
            <ResultStat label="Scanned" value={lastResult.totals.scanned} />
            <ResultStat label="Created" value={lastResult.totals.created} />
            <ResultStat label="Updated" value={lastResult.totals.updated} />
            <ResultStat label="Verified" value={lastResult.totals.auto_verified} />
            <ResultStat label="Published" value={lastResult.totals.auto_published} />
            <ResultStat label="Review" value={lastResult.totals.review_needed} />
            <ResultStat label="Hidden" value={lastResult.totals.expired_hidden} />
            <ResultStat label="Notifications" value={lastResult.notifications_created} />
            <ResultStat label="Errors" value={lastResult.totals.errors} />
          </div>
        </div>
      )}
    </div>
  );
}
